import { createUploadRequest, getUploadStatus, UploadStatus } from "@/api/data";
import DataUploadNotification from "@/components/DataUploadNotification/DataUploadNotification";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import { CheckOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { message, Space } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export default function useValidationMutations({
  parentPendingUUID,
  validate = true,
  onCompleteFn,
}: {
  parentPendingUUID?: string;
  validate?: boolean;
  onCompleteFn?: () => void;
}) {
  const [UUID, setUUID] = useState<string>();
  const [pendingUUID, setPendingUUID] = useState<string | undefined>(parentPendingUUID);
  const { updateYear } = useDashboardYear();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<UploadStatus>("pending");

  const createUploadRequestMutation = useMutation({
    mutationFn: createUploadRequest,
    onSuccess: ({ data }) => {
      if (data.result.UUID) {
        setPendingUUID(data.result.UUID);
      }
    },
    onError: () => {
      message.error("Error creating saving/validating data.");
    },
  });

  /**
   * If a UUID is pending, we check whether its done uploading every 500ms
   */
  useQuery({
    queryKey: ["checkPendingUpload", pendingUUID],
    queryFn: () => getUploadStatusMutation.mutateAsync({ UUID: pendingUUID || "" }),
    refetchInterval: 500,
    enabled: !!pendingUUID && validate,
  });

  const getUploadStatusMutation = useMutation({
    mutationFn: getUploadStatus,
    onSuccess: ({ data }, variables) => {
      setProgress(data.result.progress || 0);
      setStatus(data.result.status || "pending");
      if (data.result.status === "success") {
        const orders_per_year = data.result.meta_data?.orders_per_year;
        const total_facilities_updated = data.result.meta_data?.total_facilities_updated;
        const total_new_facilities = data.result.meta_data?.total_new_facilities;
        const business_travel_per_year = data.result.meta_data?.business_travel_per_year;
        const commute_per_year = data.result.meta_data?.commute_per_year;
        const transport_per_year = data.result.meta_data?.transport_per_year;

        const navigateWithYearChange = ({ url, year }: { url: string; year: number }) => {
          navigate(url);
          updateYear(year);
        };

        if (orders_per_year) {
          DataUploadNotification({
            description: (
              <Space direction={"vertical"}>
                {Object.keys(orders_per_year).map((year) => {
                  return (
                    <Space
                      style={{ cursor: "pointer" }}
                      key={year}
                    >
                      <CheckOutlined style={{ color: "#52C41A" }} />
                      <span
                        onClick={() =>
                          navigateWithYearChange({
                            url: "/orders/purchase_orders",
                            year: Number(year),
                          })
                        }
                      >
                        {orders_per_year[year]} Orders for{" "}
                        <span style={{ textDecoration: "underline" }}>{year}</span>
                      </span>
                    </Space>
                  );
                })}
              </Space>
            ),
          });
        }

        if (total_facilities_updated || total_new_facilities) {
          DataUploadNotification({
            description: (
              <Space direction={"vertical"}>
                {(total_facilities_updated || 0) > 0 && (
                  <Space>
                    <CheckOutlined style={{ color: "#52C41A" }} />
                    <span>{`${total_facilities_updated} facilit${
                      (total_facilities_updated || 0) > 1 ? "ies" : "y"
                    } updated`}</span>
                  </Space>
                )}
                {(total_new_facilities || 0) > 0 && (
                  <Space
                    onClick={() => navigate("/facilities?showUnrelated=1")}
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                  >
                    <CheckOutlined style={{ color: "#52C41A" }} />
                    <span>
                      {`${total_new_facilities} new facilit${
                        (total_new_facilities || 0) > 1 ? "ies" : "y"
                      }`}
                    </span>
                  </Space>
                )}
              </Space>
            ),
          });
        }

        if (business_travel_per_year) {
          DataUploadNotification({
            description: (
              <Space direction={"vertical"}>
                {Object.keys(business_travel_per_year).map((year) => {
                  return (
                    <Space
                      style={{ cursor: "pointer" }}
                      key={year}
                    >
                      <CheckOutlined style={{ color: "#52C41A" }} />
                      <span
                        onClick={() =>
                          navigateWithYearChange({
                            url: "/employees/business_travel",
                            year: Number(year),
                          })
                        }
                      >
                        {business_travel_per_year[year]} business travels for{" "}
                        <span style={{ textDecoration: "underline" }}>{year}</span>
                      </span>
                    </Space>
                  );
                })}
              </Space>
            ),
          });
        }

        if (commute_per_year) {
          DataUploadNotification({
            description: (
              <Space direction={"vertical"}>
                {Object.keys(commute_per_year).map((year) => {
                  return (
                    <Space
                      style={{ cursor: "pointer" }}
                      key={year}
                    >
                      <CheckOutlined style={{ color: "#52C41A" }} />
                      <span
                        onClick={() =>
                          navigateWithYearChange({
                            url: "/employees/commute",
                            year: Number(year),
                          })
                        }
                      >
                        {commute_per_year[year]} commutes for{" "}
                        <span style={{ textDecoration: "underline" }}>{year}</span>
                      </span>
                    </Space>
                  );
                })}
              </Space>
            ),
          });
        }

        if (transport_per_year) {
          DataUploadNotification({
            description: (
              <Space direction={"vertical"}>
                {Object.keys(transport_per_year).map((year) => {
                  return (
                    <Space
                      style={{ cursor: "pointer" }}
                      key={year}
                    >
                      <CheckOutlined style={{ color: "#52C41A" }} />
                      <span
                        onClick={() =>
                          navigateWithYearChange({
                            url: "/transport/inbound",
                            year: Number(year),
                          })
                        }
                      >
                        Transport for {transport_per_year[year]} orders for{" "}
                        <span style={{ textDecoration: "underline" }}>{year}</span>
                      </span>
                    </Space>
                  );
                })}
              </Space>
            ),
          });
        }

        if (pendingUUID) {
          onCompleteFn?.();
          setUUID(pendingUUID);
          setPendingUUID(undefined);
        }
      }
    },
  });

  const isLoading = !UUID || !!pendingUUID || createUploadRequestMutation.isLoading;

  const cancelValidation = () => {
    setUUID(undefined);
    setPendingUUID(undefined);
  };

  return {
    UUID,
    pendingUUID,
    isLoading,
    createUploadRequestMutation,
    cancelValidation,
    progress,
    status,
    setStatus,
    setProgress,
  };
}
